import React, { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { Select } from '../../components/ui/Select/Select';
import type { SelectOption } from '../../components/ui/Select/Select';
import { DownloadIcon, TrashBinIcon } from '../../assets/icons/icons';
import { Modal, ModalActions } from '../../components/ui/Modal/Modal';
import {
    DateRangePicker,
    formatDateRangeLabel,
    formatRuDate,
    fromIsoDate,
    pluralRu,
    toIsoDate,
    DateRange,
    IsoDate
} from '../../components/ui/DatePicker';

import { REPORT_FILES, useDownloadReportFileMutation } from '../../entities/reports/api/reportsApi';
import {
    useCreateAnalyticsExportMutation,
    useDeleteAnalyticsExportMutation,
    useEstimateAnalyticsQuery,
    useGetAnalyticsExportsQuery,
    useGetAnalyticsLinkMutation,
    useLazyEstimateAnalyticsQuery,
} from '../../entities/analytics/api/analyticsApi';
import type { AnalyticsExport, AnalyticsExportStatus } from '../../entities/analytics/model/types';
import { getApiErrorMessage, getConflictExport } from '../../entities/analytics/lib/apiError';
import { formatRangeLabel as formatPageRange } from '../../utils/format';
import styles from './ReportsPage.module.scss';
import { DataTable, tableStyles } from '../../components/ui/DataTable/DataTable';
import { SortableTh } from '../../components/ui/DataTable/SortableTh';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { DEFAULT_PAGE_SIZE } from '../../shared/config/pagination';
import { FilterSelect } from '../../components/ui/FilterBar/FilterBar';

type StatusFilter = '' | AnalyticsExportStatus;

type SortOrder = 'asc' | 'desc';

type ReportFile = (typeof REPORT_FILES)[number];

/** (UTC+5)*/
const ANALYTICS_TIME_ZONE = 'Asia/Yekaterinburg';
const POLL_INTERVAL_MS = 5000;
const HIGHLIGHT_MS = 6000;

const STATUS_LABEL: Record<AnalyticsExportStatus, string> = {
    queued: 'В очереди',
    building: 'Собирается',
    ready: 'Готово',
    failed: 'Ошибка',
};

const STATUS_CLASS: Record<AnalyticsExportStatus, string> = {
    queued: styles.badgeQueued,
    building: styles.badgeBuilding,
    ready: styles.badgeReady,
    failed: styles.badgeFailed,
};

const STATUS_OPTIONS: SelectOption<StatusFilter>[] = [
    { value: '', label: 'Все статусы' },
    { value: 'queued', label: STATUS_LABEL.queued },
    { value: 'building', label: STATUS_LABEL.building },
    { value: 'ready', label: STATUS_LABEL.ready },
    { value: 'failed', label: STATUS_LABEL.failed },
];

const numberFormatter = new Intl.NumberFormat('ru-RU');

const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: ANALYTICS_TIME_ZONE,
});

const formatNumber = (value: number | null): string =>
    value === null ? '—' : numberFormatter.format(value);

const formatDateTime = (value: string | null): string =>
    value ? dateTimeFormatter.format(new Date(value)) : '—';

const formatFileSize = (bytes: number | null): string => {
    if (bytes === null) {
        return '—';
    }

    if (bytes < 1024) {
        return `${bytes} Б`;
    }

    const units = ['КБ', 'МБ', 'ГБ'];
    let value = bytes / 1024;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
};

const isActive = (status: AnalyticsExportStatus): boolean =>
    status === 'queued' || status === 'building';

const toTimestamp = (value: string | null): number => (value ? new Date(value).getTime() : 0);

interface DownloadButtonProps {
    onClick: () => void;
    children: ReactNode;
    disabled?: boolean;
    loading?: boolean;
    /** primary — синяя; ghost — контурная в карточках; link — текстовая в таблице */
    variant?: 'primary' | 'ghost' | 'link';
}

const BUTTON_VARIANT_CLASS: Record<NonNullable<DownloadButtonProps['variant']>, string> = {
    primary: styles.buttonPrimary,
    ghost: styles.buttonGhost,
    link: styles.buttonLink,
};

const DownloadButton = ({
    onClick,
    children,
    disabled = false,
    loading = false,
    variant = 'primary',
}: DownloadButtonProps) => (
    <button
        type="button"
        className={`${styles.button} ${BUTTON_VARIANT_CLASS[variant]}`}
        onClick={onClick}
        disabled={disabled || loading}
    >
        {loading ? (
            <svg className={styles.spinner} viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="7" />
            </svg>
        ) : (
            <DownloadIcon className={styles.icon} />
        )}
        {loading ? 'Готовим файл' : children}
    </button>
);

/** Форма «заказать выгрузку за период» с проверкой объёма до отправки. */
const AnalyticsOrderCard = ({ onConflict }: { onConflict: (item: AnalyticsExport) => void }) => {
    const notify = useToast();
    const [range, setRange] = useState<DateRange | null>(null);
    const [tillMax, setTillMax] = useState<Date | null>(null);

    /* Пределы (max_months) берём с бэка, а не зашиваем: один лёгкий запрос за сегодня. */
    const todayIso = useMemo(() => toIsoDate(new Date()), []);
    const { data: limits } = useEstimateAnalyticsQuery({ since: todayIso, till: todayIso });
    const maxMonths = limits?.maxMonths ?? null;

    const {
        data: estimate,
        isFetching: isEstimating,
        isError: isEstimateError,
    } = useEstimateAnalyticsQuery(range ? { since: range.start, till: range.end } : skipToken);

    const [fetchEstimate] = useLazyEstimateAnalyticsQuery();
    const [createExport, { isLoading: isOrdering }] = useCreateAnalyticsExportMutation();

    const isNotAllowed = estimate ? !estimate.allowed : false;
    const canOrder = Boolean(range) && !isOrdering && !isEstimating && !isNotAllowed;

    /* till_max зависит только от since — запрашиваем сразу после выбора начала
       и ограничиваем им календарь. Если не получилось, бэк всё равно проверит
       период при полном выборе. */
    const handleStartPick = async (start: IsoDate) => {
        setTillMax(null);

        try {
            const result = await fetchEstimate({ since: start, till: start }).unwrap();
            setTillMax(fromIsoDate(result.tillMax));
        } catch {
            /* без ограничения — проверка ниже всё равно сработает */
        }
    };

    const handleRangeChange = (next: DateRange | null) => {
        setRange(next);
        setTillMax(null);
    };

    // Результат проверки периода показываем тостом, а не в карточке.
    useEffect(() => {
        if (!range || isEstimating) return;

        if (estimate && !estimate.allowed) {
            notify(estimate.reason ?? 'Период не подходит. Выберите другой.');
        } else if (!estimate && isEstimateError) {
            notify('Не удалось проверить период.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, estimate, isEstimating, isEstimateError]);

    const handleOrder = async () => {
        if (!range) return;

        try {
            await createExport({ since: range.start, till: range.end }).unwrap();
            setRange(null);
        } catch (error) {
            const conflict = getConflictExport(error);
            if (conflict) onConflict(conflict);

            notify(getApiErrorMessage(error, 'Не удалось заказать выгрузку. Попробуйте ещё раз.'));
        }
    };

    return (
        <article className={`${styles.card} ${styles.cardWide}`}>
            <div className={styles.cardBody}>
                <h2 className={styles.cardTitle}>Полная аналитика</h2>
                <p className={styles.cardText}>
                    Обращения к платформе и активность организаций за выбранный период (не более 3 месяцев)
                </p>
            </div>

            <div className={styles.cardRow}>
                <div className={styles.cardField}>
                    <DateRangePicker
                        value={range}
                        endMaxDate={tillMax}
                        maxMonths={maxMonths}
                        onStartPick={handleStartPick}
                        onChange={handleRangeChange}
                        disabled={isOrdering}
                    />

                    <p className={styles.cardMeta}>
                        {range ? (
                            <>
                                Выбранный период: <b>{formatDateRangeLabel(range)}</b>
                                <button
                                    type="button"
                                    className={styles.linkButton}
                                    onClick={() => handleRangeChange(null)}
                                    disabled={isOrdering}
                                >
                                    Сбросить
                                </button>
                            </>
                        ) : (
                            'Максимальный период — 3 месяца'
                        )}
                    </p>
                </div>

                <div className={styles.cardControls}>
                    <button
                        type="button"
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={handleOrder}
                        disabled={!canOrder}
                    >
                        {isOrdering ? (
                            <svg className={styles.spinner} viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="7" />
                            </svg>
                        ) : (
                            <DownloadIcon className={styles.icon} />
                        )}
                        {isOrdering ? 'Формирование выгрузки' : 'Сформировать выгрузку'}
                    </button>
                </div>
            </div>
        </article>
    );
};

interface ConfirmDeleteDialogProps {
    item: AnalyticsExport | null;
    isLoading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const ConfirmDeleteDialog = ({ item, isLoading, onCancel, onConfirm }: ConfirmDeleteDialogProps) => (
    <Modal
        open={Boolean(item)}
        width={420}
        tone="danger"
        title="Удалить выгрузку?"
        description={
            item
                ? `Выгрузка за ${formatRuDate(item.since)} — ${formatRuDate(item.till)} и её файл удалятся без возможности восстановить.`
                : undefined
        }
        icon={<TrashBinIcon />}
        onClose={onCancel}
        footer={
            <ModalActions>
                <Button onClick={onCancel} disabled={isLoading}>
                    Отмена
                </Button>
                <Button variant="danger" onClick={onConfirm} disabled={isLoading}>
                    {isLoading ? 'Удаляем…' : 'Удалить'}
                </Button>
            </ModalActions>
        }
    />
);

interface AnalyticsExportsProps {
    highlightId: string | null;
}

const AnalyticsExports = ({ highlightId }: AnalyticsExportsProps) => {
    const notify = useToast();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [status, setStatus] = useState<StatusFilter>('');
    const [createdOrder, setCreatedOrder] = useState<SortOrder>('desc');
    const [isPolling, setIsPolling] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<AnalyticsExport | null>(null);

    const { data, isLoading, isFetching, isError, refetch } = useGetAnalyticsExportsQuery(
        { pageNum: page, pageSize, status: status || undefined },
        { pollingInterval: isPolling ? POLL_INTERVAL_MS : 0 },
    );

    const [getLink, { isLoading: isLinkLoading, originalArgs: linkArgs }] = useGetAnalyticsLinkMutation();
    const [deleteExport, { isLoading: isDeleting }] = useDeleteAnalyticsExportMutation();

    // Пока что-то в очереди или собирается — тихо обновляем список.
    useEffect(() => {
        setIsPolling(Boolean(data?.items.some((item) => isActive(item.status))));
    }, [data]);

    useEffect(() => {
        if (!highlightId) {
            return;
        }

        document
            .getElementById(`analytics-export-${highlightId}`)
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [highlightId, data]);

    /* Сортировка по дате создания. Пока — в пределах текущей страницы.
       Когда бэк начнёт принимать сортировку, передайте createdOrder в запрос
       (например, sort_by: 'created_at', order_by: createdOrder) и уберите useMemo. */
    const items = useMemo(() => {
        const list = data?.items ?? [];
        const sign = createdOrder === 'asc' ? 1 : -1;

        return [...list].sort((a, b) => sign * (toTimestamp(a.createdAt) - toTimestamp(b.createdAt)));
    }, [data, createdOrder]);

    const total = data?.count ?? 0;
    const pageCount = Math.max(1, data?.pages ?? 1);

    const changePage = (next: number) => setPage(Math.min(Math.max(1, next), pageCount));

    const changePerPage = (value: number) => {
        setPageSize(value);
        setPage(1);
    };

    const changeStatus = (value: StatusFilter) => {
        setStatus(value);
        setPage(1);
    };

    const toggleCreatedOrder = () => setCreatedOrder((order) => (order === 'asc' ? 'desc' : 'asc'));

    const handleDownload = async (item: AnalyticsExport) => {
        try {
            // Ссылка временная — берём её в момент нажатия и никуда не сохраняем.
            const { url, fileName } = await getLink(item.id).unwrap();
            const link = document.createElement('a');

            link.href = url;
            link.download = fileName;
            link.rel = 'noopener';
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            notify(getApiErrorMessage(error, 'Не удалось получить ссылку на файл.'));
        }
    };

    const handleDelete = async () => {
        if (!pendingDelete) {
            return;
        }

        try {
            await deleteExport(pendingDelete.id).unwrap();
        } catch (error) {
            notify(getApiErrorMessage(error, 'Не удалось удалить выгрузку.'));
        } finally {
            setPendingDelete(null);
        }
    };

    return (
        <section className={styles.journal}>
            <header className={styles.journalHeader}>
                <h2 className={styles.journalTitle}>Заказанные выгрузки</h2>

                <div className={styles.filters}>
                    {data && <span className={styles.journalCount}>Всего: {formatNumber(total)}</span>}

                    <Select
                        className={styles.select}
                        value={status}
                        options={STATUS_OPTIONS}
                        onChange={changeStatus}
                        aria-label="Фильтр по статусу"
                    />
                </div>
            </header>

            <div className={styles.journalBody}>
                {isError ? (
                    <div className={styles.state}>
                        <p>Не удалось загрузить список выгрузок.</p>
                        <Button onClick={() => refetch()}>Повторить</Button>
                    </div>
                ) : (
                    <DataTable
                        static
                        loading={isFetching && !isPolling}
                        isLoading={isLoading}
                        loadingText="Загружаем выгрузки…"
                        scrollKey={`${page}-${pageSize}-${status}-${createdOrder}`}
                        footer={
                            total > 0 ? (
                                <TableFooter
                                    page={page}
                                    totalPages={pageCount}
                                    rangeLabel={formatPageRange(page, pageSize, total)}
                                    onPageChange={changePage}
                                />
                            ) : undefined
                        }
                        empty={
                            data && items.length === 0 ? (
                                <div className={tableStyles.emptyState}>
                                    <b>
                                        {status
                                            ? 'С таким статусом выгрузок нет.'
                                            : 'Выгрузок пока нет. Закажите первую — она появится здесь.'}
                                    </b>
                                </div>
                            ) : undefined
                        }
                    >
                        <thead>
                            <tr>
                                <th scope="col">Период</th>
                                <th scope="col">Статус</th>
                                <SortableTh
                                    title="Создана"
                                    active
                                    dir={createdOrder}
                                    onClick={toggleCreatedOrder}
                                />
                                <th scope="col">Создал</th>
                                <th scope="col">Файл</th>
                                <th scope="col" className={styles.thActions}>
                                    Действия
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {items.map((item) => (
                                <tr
                                    key={item.id}
                                    id={`analytics-export-${item.id}`}
                                    className={highlightId === item.id ? styles.rowHighlight : undefined}
                                >
                                    <td className={styles.cellNums}>
                                        {formatRuDate(item.since)} — {formatRuDate(item.till)}
                                    </td>

                                    <td>
                                        <span className={`${styles.badge} ${STATUS_CLASS[item.status]}`}>
                                            {STATUS_LABEL[item.status]}
                                        </span>
                                        {item.status === 'failed' && item.error && (
                                            <span className={styles.errorText}>{item.error}</span>
                                        )}
                                    </td>

                                    <td className={styles.cellNums}>{formatDateTime(item.createdAt)}</td>

                                    <td>
                                        <span className={styles.reportTitle}>
                                            {item.authorName || item.authorEmail}
                                        </span>
                                        {item.authorName && (
                                            <span className={styles.reportKind}>{item.authorEmail}</span>
                                        )}
                                    </td>

                                    <td className={styles.cellNums}>
                                        {item.status === 'ready' ? (
                                            <>
                                                {formatFileSize(item.fileSize)}
                                                <span className={styles.subText}>
                                                    {formatNumber(item.rows)}
                                                    {item.rows !== null &&
                                                        ` ${pluralRu(item.rows, ['строка', 'строки', 'строк'])}`}
                                                    {item.sheets > 1 &&
                                                        ` · ${item.sheets} ${pluralRu(item.sheets, ['лист', 'листа', 'листов'])}`}
                                                </span>
                                            </>
                                        ) : (
                                            '—'
                                        )}
                                    </td>

                                    <td className={tableStyles.actions}>
                                        <div className={styles.rowActions}>
                                            <DownloadButton
                                                variant="link"
                                                disabled={item.status !== 'ready'}
                                                loading={isLinkLoading && linkArgs === item.id}
                                                onClick={() => handleDownload(item)}
                                            >
                                                Скачать
                                            </DownloadButton>
                                            <button
                                                type="button"
                                                className={styles.iconDanger}
                                                disabled={item.status === 'building'}
                                                aria-label="Удалить"
                                                title={
                                                    item.status === 'building'
                                                        ? 'Пока идёт сборка, удалить нельзя'
                                                        : 'Удалить'
                                                }
                                                onClick={() => setPendingDelete(item)}
                                            >
                                                <TrashBinIcon className={styles.icon} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                )}
            </div>

            <ConfirmDeleteDialog
                item={pendingDelete}
                isLoading={isDeleting}
                onCancel={() => setPendingDelete(null)}
                onConfirm={handleDelete}
            />
        </section>
    );
};

export const ReportsPage = () => {
    const notify = useToast();
    const [highlightId, setHighlightId] = useState<string | null>(null);

    const [downloadFile, { isLoading: isFileLoading, originalArgs }] = useDownloadReportFileMutation();

    useEffect(() => {
        if (!highlightId) {
            return;
        }

        const timer = window.setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);

        return () => window.clearTimeout(timer);
    }, [highlightId]);

    const handleDownloadFile = async (file: ReportFile) => {
        try {
            await downloadFile(file).unwrap();
        } catch {
            notify('Файл не сформировался. Попробуйте ещё раз.');
        }
    };

    return (
        <div className={styles.page}>
            <PageHeader title="Аналитика" />

            <div className={styles.flex}>
                <div className={styles.cards}>
                    <AnalyticsOrderCard onConflict={(item) => setHighlightId(item.id)} />

                    {REPORT_FILES.map((file) => (
                        <article className={styles.card} key={file.id}>
                            <div className={styles.cardBody}>
                                <h2 className={styles.cardTitle}>{file.title}</h2>
                                <p className={styles.cardText}>{file.description}</p>
                            </div>

                            <div className={styles.cardControls}>
                                <DownloadButton
                                    variant="ghost"
                                    loading={isFileLoading && originalArgs?.id === file.id}
                                    onClick={() => handleDownloadFile(file)}
                                >
                                    Скачать Excel
                                </DownloadButton>
                            </div>
                        </article>
                    ))}
                </div>

                <section className={styles.pane}>
                    <AnalyticsExports highlightId={highlightId} />
                </section>
            </div>
        </div>
    );
};